import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BadgeCheck,
  Building2,
  Clock,
  Globe,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Store,
  User as UserIcon,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth, useReference } from '@/hooks/useApp';
import { useSeo } from '@/hooks/useSeo';
import { Badge, Button, EmptyState, Spinner } from '@/components/ui';
import { CarCard } from '@/components/CarCard';
import { StarRating } from '@/components/StarRating';
import { RatingSummaryPanel, SellerReviews } from '@/components/Reviews';
import { formatDate, formatNumber } from '@/lib/format';
import { SITE_NAME, plural } from '@/lib/seo';
import { SellerType, isBusinessSeller } from '@/lib/types';
import type { ListingSummary, PagedResult, SellerProfile } from '@/lib/types';

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();
}

function Stat({ value, label }: { value: string | number; label: string }) {
  return (
    <div className="flex flex-col">
      <span className="font-display text-xl font-bold text-body tabular-nums">{value}</span>
      <span className="text-[11px] text-faint">{label}</span>
    </div>
  );
}

/** One line of the contact block. Renders nothing at all when the seller left the field blank. */
function Detail({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-sunken text-muted">
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-[11px] text-faint">{label}</p>
        <div className="text-sm break-words text-body">{children}</div>
      </div>
    </div>
  );
}

/** Strips the scheme so the link reads as a domain rather than as a URL. */
function prettyUrl(url: string): string {
  return url.replace(/^https?:\/\//i, '').replace(/\/$/, '');
}

/**
 * Who you are dealing with.
 *
 * Reachable from every listing, because "is this seller any good?" is a question a buyer asks
 * before "is this car any good?" — and until now the answer lived nowhere.
 */
export function SellerProfilePage() {
  const { sellerId = '' } = useParams();
  const { data: reference } = useReference();
  const { user } = useAuth();

  const { data: seller, isPending, isError } = useQuery({
    queryKey: ['seller', sellerId],
    queryFn: () => api<SellerProfile>(`/api/sellers/${sellerId}`),
    enabled: Boolean(sellerId),
  });

  const { data: listings } = useQuery({
    queryKey: ['seller-listings', sellerId],
    queryFn: () =>
      api<PagedResult<ListingSummary>>(`/api/listings?sellerId=${sellerId}&pageSize=12`),
    enabled: Boolean(seller),
  });

  useSeo(
    seller
      ? {
          title: `${seller.dealershipName ?? seller.displayName} — polovni automobili | ${SITE_NAME}`,
          description:
            `${seller.dealershipName ?? seller.displayName}` +
            (seller.cityName ? ` — ${seller.cityName}` : '') +
            `. ${formatNumber(seller.activeListingCount)} ` +
            `${plural(seller.activeListingCount, 'aktivan oglas', 'aktivna oglasa', 'aktivnih oglasa')} ` +
            `na ${SITE_NAME}.`,
          canonicalPath: `/prodavac/${sellerId}`,
          image: seller.profilePhotoUrl ?? undefined,

          // Nothing for sale is nothing to index. The page stays reachable and its links are still
          // followed; it just does not enter the index until the seller posts something.
          noIndex: seller.activeListingCount === 0,
        }
      : null,
  );

  if (isPending) {
    return (
      <div className="flex justify-center py-24">
        <Spinner className="size-8 text-accent" />
      </div>
    );
  }

  if (isError || !seller) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16">
        <EmptyState
          title="Prodavac nije pronađen"
          description="Nalog je možda uklonjen."
          action={
            <Link to="/">
              <Button>Nazad na pretragu</Button>
            </Link>
          }
        />
      </div>
    );
  }

  const name = seller.dealershipName ?? seller.displayName;
  const isBusiness = isBusinessSeller(seller.sellerType);
  const isOwnProfile = user?.id === seller.id;
  const sellerTypeLabel = reference?.enums.sellerTypes.find(
    (option) => option.value === seller.sellerType,
  )?.label;

  const TypeIcon = seller.sellerType === SellerType.Showroom ? Building2 : Store;

  const hasContactBlock = Boolean(
    seller.contactPhone ||
      seller.publicEmail ||
      seller.businessAddress ||
      seller.businessHours ||
      seller.websiteUrl,
  );

  return (
    <div className="relative overflow-hidden">
      <div className="aurora">
        <span />
      </div>

      <div className="relative mx-auto max-w-5xl px-4 py-8">
        <header className="animate-fade-in-up flex flex-col gap-5 rounded-[var(--radius-panel)] border border-default bg-raised p-6 shadow-card sm:flex-row sm:items-center">
          {seller.profilePhotoUrl ? (
            <img
              src={seller.profilePhotoUrl}
              alt=""
              className="size-20 shrink-0 rounded-2xl object-cover"
            />
          ) : (
            <span className="flex size-20 shrink-0 items-center justify-center rounded-2xl bg-accent-soft font-display text-2xl font-bold text-accent">
              {initials(name)}
            </span>
          )}

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-2xl font-bold text-body">{name}</h1>
              {isBusiness ? (
                <Badge tone="accent">
                  <TypeIcon size={12} />
                  {sellerTypeLabel ?? 'Auto plac'}
                </Badge>
              ) : (
                <Badge tone="neutral">
                  <UserIcon size={12} />
                  {sellerTypeLabel ?? 'Fizičko lice'}
                </Badge>
              )}
              {seller.phoneVerified && (
                <Badge tone="success">
                  <BadgeCheck size={12} />
                  Verifikovan
                </Badge>
              )}
            </div>

            {seller.rating.count > 0 && (
              <div className="mt-2 flex items-center gap-2">
                <StarRating value={seller.rating.average ?? 0} size="sm" />
                <span className="text-sm font-medium text-body tabular-nums">
                  {(seller.rating.average ?? 0).toFixed(1).replace('.', ',')}
                </span>
                <span className="text-xs text-faint tabular-nums">
                  ({seller.rating.count})
                </span>
              </div>
            )}

            {seller.bio && <p className="mt-2 text-sm leading-relaxed text-muted">{seller.bio}</p>}

            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-faint">
              {seller.cityName && (
                <span className="flex items-center gap-1">
                  <MapPin size={12} />
                  {seller.cityName}
                </span>
              )}
              <span>Član od {formatDate(seller.memberSince)}</span>
            </div>
          </div>

          <div className="flex gap-6 sm:flex-col sm:gap-3 sm:border-l sm:border-default sm:pl-6">
            <Stat value={seller.activeListingCount} label="aktivnih oglasa" />
            <Stat value={seller.soldListingCount} label="prodatih" />

            {isOwnProfile && (
              <Link to="/profil" className="self-center sm:self-stretch">
                <Button variant="secondary" size="sm" className="w-full">
                  <Pencil size={14} />
                  Uredi
                </Button>
              </Link>
            )}
          </div>
        </header>

        {/*
          The shop window.

          A buyer deciding whether to drive across the country to see a car wants the address, the
          opening hours and a number to ring first — and until this block existed, a plac had
          nowhere to put any of it. Rendered only when there is something to render, so a private
          seller's page does not grow an empty panel.
        */}
        {hasContactBlock && (
          <section className="animate-fade-in-up mt-6 rounded-[var(--radius-panel)] border border-default bg-raised p-6 shadow-card">
            <h2 className="mb-4 text-lg font-semibold text-body">Kontakt</h2>

            <div className="grid gap-4 sm:grid-cols-2">
              {seller.businessAddress && (
                <Detail icon={<MapPin size={15} />} label="Adresa">
                  {seller.businessAddress}
                  {seller.cityName && `, ${seller.cityName}`}
                </Detail>
              )}

              {seller.contactPhone && (
                <Detail icon={<Phone size={15} />} label="Telefon">
                  <a href={`tel:${seller.contactPhone}`} className="hover:text-accent">
                    {seller.contactPhone}
                  </a>
                </Detail>
              )}

              {seller.businessHours && (
                <Detail icon={<Clock size={15} />} label="Radno vreme">
                  {seller.businessHours}
                </Detail>
              )}

              {seller.publicEmail && (
                <Detail icon={<Mail size={15} />} label="E-mail">
                  <a href={`mailto:${seller.publicEmail}`} className="hover:text-accent">
                    {seller.publicEmail}
                  </a>
                </Detail>
              )}

              {seller.websiteUrl && (
                <Detail icon={<Globe size={15} />} label="Veb-sajt">
                  {/*
                    noopener because a new tab would otherwise get a handle on this window, and
                    nofollow because an outbound link from every dealer page is exactly the kind of
                    thing a link farm is built out of.
                  */}
                  <a
                    href={seller.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="hover:text-accent"
                  >
                    {prettyUrl(seller.websiteUrl)}
                  </a>
                </Detail>
              )}
            </div>

            {(seller.taxNumber || seller.companyNumber) && (
              <p className="mt-5 border-t border-default pt-4 text-xs text-faint tabular-nums">
                {seller.taxNumber && <>PIB: {seller.taxNumber}</>}
                {seller.taxNumber && seller.companyNumber && <span className="mx-2">·</span>}
                {seller.companyNumber && <>Matični broj: {seller.companyNumber}</>}
              </p>
            )}
          </section>
        )}

        {listings && listings.items.length > 0 && (
          <section className="mt-10">
            <h2 className="mb-4 text-lg font-semibold text-body">Aktivni oglasi</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {listings.items.map((listing, index) => (
                <CarCard key={listing.id} listing={listing} index={index} />
              ))}
            </div>
          </section>
        )}

        <section className="mt-12">
          <h2 className="mb-4 text-lg font-semibold text-body">Ocene prodavca</h2>
          <div className="space-y-4">
            {seller.rating.count > 0 && (
              <RatingSummaryPanel summary={seller.rating} label="ocena" />
            )}
            <SellerReviews sellerId={seller.id} compact />
          </div>
        </section>
      </div>
    </div>
  );
}
